import React, { type ReactNode } from 'react';
import Modal from './Modal/Modal';
import ModalTrigger from './Modal/ModalTrigger';
import ModalContent from './Modal/ModalContent';
import AppModal from './AppModal';
import IntegracaoBox from './IntegracoesBox';
import { Button } from './Button';
import { useTranslation } from '../lib/i18n/TranslationContext';
import { type AppProps } from '../lib/AppsArray';
import { twMerge } from 'tailwind-merge';

const trackClick = (app: string) => {
	fetch(`https://tools.empreender.com.br/produtos-empreender-click?app=${app}&origem=CLICK_QUALQUER`, {
		method: 'POST',
	});
};

interface previewProps {
	eplusApps: {
		[key: string]: {
			integrations?: Integration;
		};
	};
	app: {
		icon: ReactNode;
		name: ReactNode;
		content: ReactNode;
		eplus: string;
		integrations?: Integration;
		link?: string;
		hide?: boolean;
		suportLink?: string;
		slug?: string;
	};
}

interface iconProps {
	width: number;
	height: number;
}

interface Integration {
	[key: string]: {
		show: boolean;
		img: string;
	};
}

type AppPreviewProps = previewProps &
	AppProps & {
		hideActions?: boolean;
	};

const AppPreview: React.FC<AppPreviewProps> = ({ eplusApps, app, hideActions = false }) => {
	const { t } = useTranslation();

	return (
		<div
			className={twMerge(
				'w-[300px] !p-5 bg-white shadow-[0px_4px_20px_-6px_rgba(0,_0,_0,_0.12)] border overflow-hidden rounded-lg',
				hideActions ? 'h-auto' : 'h-[282px]',
			)}
		>
			<ul className='delay-[50ms] h-full group-data-[motion^=from-]/animation:animate-in group-data-[motion^=to-]/animation:animate-out group-data-[motion^=from-]/animation:fade-in group-data-[motion^=to-]/animation:fade-out group-data-[motion=from-end]/animation:slide-in-from-top-52 group-data-[motion=from-start]/animation:slide-in-from-bottom-52 group-data-[motion=to-end]/animation:slide-out-to-top-52 group-data-[motion=to-start]/animation:slide-out-to-bottom-52'>
				<div className={twMerge('w-full flex flex-col !gap-4 h-full', hideActions ? 'justify-start' : 'justify-between')}>
					<div className='flex flex-row items-center !gap-2'>
						<div className='size-12 bg-gray-100 rounded-lg flex items-center justify-center'>
							<span>{React.cloneElement(app.icon as React.ReactElement<iconProps>, { width: 35, height: 35 })}</span>
						</div>
						<span className='text-gray-900 font-semibold break-normal whitespace-nowrap font-base'>{t(app.name)}</span>
					</div>
					<p className='text-gray-900 text-sm'>{t(app.content as string)}</p>

					<div className='flex flex-row !gap-1.5'>
						{eplusApps &&
							eplusApps[app.eplus]?.integrations &&
							Object.entries(eplusApps[app.eplus]?.integrations || {})
								.filter(([, value]) => value.show)
								.map(([plataforma, details], index) => (
									<React.Fragment key={index}>
										<IntegracaoBox plataforma={plataforma} index={index} image={details.img} />
									</React.Fragment>
								))}
					</div>

					{!hideActions ? (
						<div className='w-full flex flex-row justify-end gap-2'>
							<Button asChild variant='outline' className='h-9'>
								<a onClick={() => trackClick(app.eplus)} href={`${app.link}`} target='_blank' rel='noopener noreferrer'>
									{t('Saiba mais')}
								</a>
							</Button>
							{Object.keys(eplusApps).length > 0 ? (
								<>
									{app.eplus === 'DASH' ? (
										<Button asChild variant='outline' className='h-9'>
											<a onClick={() => trackClick(`${app.eplus}`)} href={app.eplus === 'DASH' ? 'https://app.dash.com.br' : `https://app.empreender.plus/?app=${app.eplus}`} target='_blank' rel='noopener noreferrer'>
												{t('Entrar')}
											</a>
										</Button>
									) : (
										<Modal>
											<ModalTrigger>
												<Button variant='outline' className='h-9'>{t('Entrar')}</Button>
											</ModalTrigger>
											<ModalContent className='h-auto'>
												<AppModal app={app} />
											</ModalContent>
										</Modal>
									)}
								</>
							) : (
								<Button asChild variant='outline' className='h-9'>
									<a onClick={() => trackClick(`${app.eplus}`)} href={app.eplus === 'DASH' ? 'https://app.dash.com.br' : `https://app.empreender.plus/?app=${app.eplus}`} target='_blank' rel='noopener noreferrer'>
										{t('Entrar')}
									</a>
								</Button>
							)}
						</div>
					) : null}
				</div>
			</ul>
		</div>
	);
};

export default AppPreview;
